'use client'

import { BaseRenderer, EnvironmentRenderer, BackgroundRenderer } from '../../utils/environmentRenderer'

export class ParisBackgroundRenderer implements BackgroundRenderer {

drawBackground(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number, canvasHeight: number): void {
	// Ciel parisien
	EnvironmentRenderer.drawSkyGradient(ctx, cameraX, canvasWidth, canvasHeight, '#87CEEB', '#B0E0E6')

	// Dessiner l'arrière-plan parisien par couches
	this.drawParisLandmarks(ctx, cameraX, canvasWidth)
	this.drawParisBuildings(ctx, cameraX, canvasWidth)
	this.drawParisStreetElements(ctx, cameraX, canvasWidth)
	this.drawParisClouds(ctx, cameraX, canvasWidth)
}

drawGround(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number, canvasHeight: number): void {
	// Sol pavé parisien
	EnvironmentRenderer.drawTexturedGround(
	ctx, cameraX, canvasWidth, 360, 40, '#8B7355', '#696969', 8
	)
}

private drawParisLandmarks(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number): void {
	const startX = cameraX - 300
	const endX = cameraX + canvasWidth + 300

	// Tour Eiffel principale (tous les 2000 pixels) - très loin
	for (let x = Math.floor(startX / 2000) * 2000; x <= endX; x += 2000) {
	this.drawEiffelTower(ctx, x + 300, 180)
	}

	// Arc de Triomphe (tous les 1600 pixels, décalé) - loin
	for (let x = Math.floor(startX / 1600) * 1600; x <= endX; x += 1600) {
	this.drawArcDeTriomphe(ctx, x + 800, 240)
	}
}

private drawParisBuildings(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number): void {
	const startX = cameraX - 300
	const endX = cameraX + canvasWidth + 300

	// Plan moyen - immeubles haussmanniens variés
	for (let x = Math.floor(startX / 150) * 150; x <= endX; x += 150) {
	const variation = Math.sin(x * 0.008) * 20
	this.drawSmallParisBuilding(ctx, x + 20, 280 + variation)
	}

	// Immeubles plus hauts occasionnels
	for (let x = Math.floor(startX / 300) * 300; x <= endX; x += 300) {
	this.drawMediumParisBuilding(ctx, x + 100, 220)
	}
}

private drawParisStreetElements(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number): void {
	const startX = cameraX - 300
	const endX = cameraX + canvasWidth + 300

	// Panneaux de métro dans le décor (bien espacés)
	for (let x = Math.floor(startX / 500) * 500; x <= endX; x += 500) {
	this.drawMetroSign(ctx, x + 200, 320)
	}

	// Arbres parisiens (platanes) le long de la route
	for (let x = Math.floor(startX / 200) * 200; x <= endX; x += 200) {
	this.drawParisTree(ctx, x + 80, 300)
	}
}

private drawParisClouds(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number): void {
	const startX = cameraX - 300
	const endX = cameraX + canvasWidth + 300

	// Ciel - nuages variés
	for (let x = Math.floor(startX / 400) * 400; x <= endX; x += 400) {
	BaseRenderer.drawCloud(ctx, x + 150, 40 + (Math.sin(x * 0.003) * 15))
	}

	// Petits nuages d'appoint
	for (let x = Math.floor(startX / 250) * 250; x <= endX; x += 250) {
	BaseRenderer.drawSmallCloud(ctx, x + 50, 25 + (Math.sin(x * 0.007) * 8))
	}
}

// Fonction pour dessiner la Tour Eiffel (version plus petite pour l'arrière-plan lointain)
private drawEiffelTower(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()
	const scale = 0.7

	// Couleur dorée mais plus pâle (effet atmosphérique)
	ctx.fillStyle = 'rgba(255, 215, 0, 0.8)'

	// Base simplifiée
	const baseWidth = 40 * scale
	const baseHeight = 5 * scale
	ctx.fillRect(x - baseWidth/2, y + 90 * scale, baseWidth, baseHeight)

	// Pieds principaux (simplifiés)
	ctx.fillRect(x - 15 * scale, y + 70 * scale, 3 * scale, 20 * scale)
	ctx.fillRect(x + 12 * scale, y + 70 * scale, 3 * scale, 20 * scale)
	ctx.fillRect(x - 8 * scale, y + 60 * scale, 3 * scale, 30 * scale)
	ctx.fillRect(x + 5 * scale, y + 60 * scale, 3 * scale, 30 * scale)

	// Arc central
	ctx.fillRect(x - 10 * scale, y + 75 * scale, 20 * scale, 3 * scale)

	// Premier étage
	ctx.fillRect(x - 12 * scale, y + 55 * scale, 24 * scale, 4 * scale)

	// Structure montante
	ctx.fillRect(x - 6 * scale, y + 35 * scale, 3 * scale, 20 * scale)
	ctx.fillRect(x + 3 * scale, y + 35 * scale, 3 * scale, 20 * scale)

	// Deuxième étage
	ctx.fillRect(x - 8 * scale, y + 30 * scale, 16 * scale, 3 * scale)

	// Sommet
	ctx.fillRect(x - 2 * scale, y + 15 * scale, 4 * scale, 15 * scale)
	ctx.fillRect(x - 1 * scale, y + 5 * scale, 2 * scale, 10 * scale)

	// Antenne
	ctx.fillStyle = 'rgba(139, 69, 19, 0.8)'
	ctx.fillRect(x, y, 1 * scale, 5 * scale)

	ctx.restore()
}

// Fonction pour dessiner l'Arc de Triomphe (version arrière-plan)
private drawArcDeTriomphe(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()
	const scale = 0.8

	// Corps principal de l'arc (couleur plus pâle pour la distance)
	ctx.fillStyle = 'rgba(245, 245, 220, 0.9)'
	ctx.fillRect(x, y, 50 * scale, 65 * scale)

	// Arche centrale
	ctx.fillStyle = '#87CEEB' // Couleur du ciel pour créer l'ouverture
	ctx.fillRect(x + 12 * scale, y + 16 * scale, 26 * scale, 40 * scale)

	// Piliers
	ctx.fillStyle = 'rgba(245, 245, 220, 0.9)'
	ctx.fillRect(x, y + 16 * scale, 10 * scale, 40 * scale) // Pilier gauche
	ctx.fillRect(x + 40 * scale, y + 16 * scale, 10 * scale, 40 * scale) // Pilier droit

	// Détails décoratifs simplifiés
	ctx.fillStyle = 'rgba(210, 180, 140, 0.8)'
	ctx.fillRect(x + 4 * scale, y + 8 * scale, 42 * scale, 2 * scale) // Frise supérieure
	ctx.fillRect(x + 4 * scale, y + 20 * scale, 6 * scale, 2 * scale) // Détail pilier gauche
	ctx.fillRect(x + 40 * scale, y + 20 * scale, 6 * scale, 2 * scale) // Détail pilier droit

	// Attique (partie supérieure)
	ctx.fillStyle = 'rgba(245, 245, 220, 0.9)'
	ctx.fillRect(x + 8 * scale, y - 8 * scale, 34 * scale, 10 * scale)

	// Drapeau français stylisé au sommet (plus petit)
	ctx.fillStyle = 'rgba(0, 85, 164, 0.8)' // Bleu
	ctx.fillRect(x + 23 * scale, y - 12 * scale, 1.5 * scale, 6 * scale)
	ctx.fillStyle = 'rgba(255, 255, 255, 0.8)' // Blanc
	ctx.fillRect(x + 24.5 * scale, y - 12 * scale, 1.5 * scale, 6 * scale)
	ctx.fillStyle = 'rgba(239, 65, 53, 0.8)' // Rouge
	ctx.fillRect(x + 26 * scale, y - 12 * scale, 1.5 * scale, 6 * scale)

	ctx.restore()
}

// Fonction pour dessiner un petit immeuble parisien
private drawSmallParisBuilding(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()
	const height = 60 + (Math.sin(x * 0.02) * 10)

	// Façade principale (plus petite)
	ctx.fillStyle = '#F5F5DC'
	ctx.fillRect(x, y, 40, height)

	// Toit mansardé
	ctx.fillStyle = '#8B4513'
	ctx.fillRect(x - 1, y - 8, 42, 10)

	// Fenêtres (plus petites)
	ctx.fillStyle = '#4169E1'
	for (let row = 0; row < 2; row++) {
	for (let col = 0; col < 3; col++) {
		ctx.fillRect(x + 5 + col * 10, y + 10 + row * 15, 4, 6)
	}
	}

	// Balcon
	ctx.fillStyle = '#2F4F4F'
	ctx.fillRect(x + 3, y + 20, 34, 1)

	ctx.restore()
}

// Fonction pour dessiner un immeuble parisien de taille moyenne
private drawMediumParisBuilding(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()
	const height = 80 + (Math.sin(x * 0.015) * 15)

	// Façade principale
	ctx.fillStyle = '#F0E68C'
	ctx.fillRect(x, y, 50, height)

	// Toit mansardé plus grand
	ctx.fillStyle = '#8B4513'
	ctx.fillRect(x - 2, y - 12, 54, 15)

	// Fenêtres élégantes
	ctx.fillStyle = '#4682B4'
	for (let row = 0; row < 3; row++) {
	for (let col = 0; col < 3; col++) {
		ctx.fillRect(x + 8 + col * 12, y + 12 + row * 18, 6, 10)
		// Volets
		ctx.fillStyle = '#2F4F4F'
		ctx.fillRect(x + 6 + col * 12, y + 12 + row * 18, 2, 10)
		ctx.fillRect(x + 14 + col * 12, y + 12 + row * 18, 2, 10)
		ctx.fillStyle = '#4682B4'
	}
	}

	// Balcons avec garde-corps
	ctx.fillStyle = '#2F4F4F'
	ctx.fillRect(x + 4, y + 25, 42, 2)
	ctx.fillRect(x + 4, y + 43, 42, 2)

	// Détails de façade
	ctx.fillStyle = '#DDD'
	ctx.fillRect(x, y + height/2, 50, 2) // Corniche
	ctx.fillRect(x + 20, y, 10, height) // Avancée centrale

	ctx.restore()
}

// Fonction pour dessiner un arbre parisien (platane)
private drawParisTree(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()

	// Tronc
	ctx.fillStyle = '#8B4513'
	ctx.fillRect(x + 8, y + 20, 8, 40)

	// Feuillage - forme ronde typique des platanes taillés
	ctx.fillStyle = '#228B22'
	ctx.fillRect(x, y, 24, 30)
	ctx.fillRect(x + 3, y - 3, 18, 36)
	ctx.fillRect(x + 6, y - 6, 12, 42)

	// Variation saisonnière légère
	ctx.fillStyle = '#32CD32'
	ctx.fillRect(x + 4, y + 2, 16, 8)
	ctx.fillRect(x + 2, y + 12, 20, 6)

	// Base/bordure
	ctx.fillStyle = '#654321'
	ctx.fillRect(x + 6, y + 58, 12, 4)

	ctx.restore()
}

// Fonction pour dessiner un panneau de métro dans le décor
private drawMetroSign(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()

	// Stations de métro parisiennes célèbres
	const stations = [
	'CHÂTELET', 'OPÉRA', 'BASTILLE', 'NATION', 'RÉPUBLIQUE',
	'CONCORDE', 'INVALIDES', 'TROCADÉRO', 'MONTPARNASSE', 'GARE DU NORD'
	]
	const stationIndex = Math.floor(Math.abs(x) / 500) % stations.length
	const stationName = stations[stationIndex]

	// Base d'ancrage au sol
	ctx.fillStyle = '#696969'
	ctx.fillRect(x + 30, y + 45, 6, 8)

	// Poteau de soutien plus réaliste
	ctx.fillStyle = '#2F4F4F'
	ctx.fillRect(x + 32, y + 25, 2, 28)

	// Panneau principal - style métro parisien authentique
	const panelWidth = Math.max(45, Math.min(stationName.length * 3 + 15, 60))

	// Ombre portée pour plus de réalisme
	ctx.fillStyle = 'rgba(0,0,0,0.2)'
	ctx.fillRect(x + 2, y + 2, panelWidth, 18)

	// Bordure verte caractéristique
	ctx.fillStyle = '#228B22'
	ctx.fillRect(x, y, panelWidth, 18)

	// Bordure blanche intérieure
	ctx.fillStyle = '#FFFFFF'
	ctx.fillRect(x + 1, y + 1, panelWidth - 2, 16)

	// Fond bleu métro
	ctx.fillStyle = '#1E3A8A'
	ctx.fillRect(x + 2, y + 2, panelWidth - 4, 14)

	// Logo M stylisé (plus petit)
	ctx.fillStyle = '#FFFFFF'
	ctx.fillRect(x + 3, y + 4, 1, 8) // M - trait gauche
	ctx.fillRect(x + 4, y + 4, 1, 3) // M - trait milieu haut
	ctx.fillRect(x + 4, y + 9, 1, 3) // M - trait milieu bas
	ctx.fillRect(x + 5, y + 4, 1, 8) // M - trait droit

	// Texte simplifié de la station (représenté par des barres)
	ctx.fillStyle = '#FFFFFF'
	const textWidth = Math.min(stationName.length, 6)
	for (let i = 0; i < textWidth; i++) {
	ctx.fillRect(x + 9 + i * 3, y + 6, 1, 4)
	}

	// Flèche directionnelle
	ctx.fillRect(x + panelWidth - 6, y + 6, 3, 1)
	ctx.fillRect(x + panelWidth - 5, y + 7, 1, 3)

	// Support diagonal pour plus de réalisme
	ctx.fillStyle = '#2F4F4F'
	ctx.fillRect(x + 28, y + 18, 8, 2)

	ctx.restore()
}
}

// Fonction pour dessiner les obstacles parisiens (vélos avec cycliste énervé)
export const drawParisObstacle = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void => {
ctx.save()

// Vélo - proportions plus réalistes
const bikeCenter = x + width/2

// Roues plus grandes (typiques d'un vélo)
ctx.strokeStyle = '#1a1a1a'
ctx.lineWidth = 2
ctx.beginPath()
ctx.arc(bikeCenter - 10, y + height - 4, 6, 0, 2 * Math.PI) // Roue arrière
ctx.stroke()
ctx.beginPath()
ctx.arc(bikeCenter + 10, y + height - 4, 6, 0, 2 * Math.PI) // Roue avant
ctx.stroke()

// Rayons des roues (4 rayons principaux pour simplicité)
ctx.strokeStyle = '#666'
ctx.lineWidth = 1
for (let i = 0; i < 4; i++) {
	const angle = (i * Math.PI) / 2
	// Roue arrière
	ctx.beginPath()
	ctx.moveTo(bikeCenter - 10, y + height - 4)
	ctx.lineTo(bikeCenter - 10 + Math.cos(angle) * 5, y + height - 4 + Math.sin(angle) * 5)
	ctx.stroke()
	// Roue avant
	ctx.beginPath()
	ctx.moveTo(bikeCenter + 10, y + height - 4)
	ctx.lineTo(bikeCenter + 10 + Math.cos(angle) * 5, y + height - 4 + Math.sin(angle) * 5)
	ctx.stroke()
}

// Cadre triangulaire caractéristique
ctx.strokeStyle = '#e74c3c'
ctx.lineWidth = 3
ctx.beginPath()
// Triangle principal du cadre
ctx.moveTo(bikeCenter - 10, y + height - 4) // Centre roue arrière
ctx.lineTo(bikeCenter - 2, y + height - 20) // Tube de selle
ctx.lineTo(bikeCenter + 10, y + height - 4) // Centre roue avant
ctx.stroke()

// Tube horizontal du cadre
ctx.beginPath()
ctx.moveTo(bikeCenter - 2, y + height - 20) // Du tube de selle
ctx.lineTo(bikeCenter + 8, y + height - 14) // Vers la direction
ctx.stroke()

// Fourche avant
ctx.strokeStyle = '#2c3e50'
ctx.lineWidth = 2
ctx.beginPath()
ctx.moveTo(bikeCenter + 8, y + height - 14) // Depuis le cadre
ctx.lineTo(bikeCenter + 10, y + height - 4) // Vers le centre de la roue avant
ctx.stroke()

// Tige de selle et guidon
ctx.strokeStyle = '#7f8c8d'
ctx.lineWidth = 2
ctx.beginPath()
ctx.moveTo(bikeCenter - 2, y + height - 20) // Tige de selle
ctx.lineTo(bikeCenter - 2, y + height - 26)
ctx.stroke()
ctx.beginPath()
ctx.moveTo(bikeCenter + 8, y + height - 14) // Tige de direction
ctx.lineTo(bikeCenter + 8, y + height - 22)
ctx.stroke()

// Guidon de vélo
ctx.strokeStyle = '#34495e'
ctx.lineWidth = 2
ctx.beginPath()
ctx.moveTo(bikeCenter + 2, y + height - 22) // Guidon horizontal
ctx.lineTo(bikeCenter + 14, y + height - 22)
ctx.stroke()

// Selle
ctx.fillStyle = '#8b4513'
ctx.fillRect(bikeCenter - 5, y + height - 28, 6, 2)

// Pédales et manivelle
ctx.fillStyle = '#2c3e50'
ctx.fillRect(bikeCenter - 4, y + height - 12, 2, 1) // Axe pédalier
ctx.fillStyle = '#95a5a6'
ctx.fillRect(bikeCenter - 6, y + height - 10, 3, 1) // Pédale gauche
ctx.fillRect(bikeCenter - 1, y + height - 14, 3, 1) // Pédale droite

// Chaîne (suggérée)
ctx.strokeStyle = '#7f8c8d'
ctx.lineWidth = 1
ctx.beginPath()
ctx.moveTo(bikeCenter - 4, y + height - 12) // Depuis le pédalier
ctx.lineTo(bikeCenter - 10, y + height - 8) // Vers la roue arrière
ctx.stroke()

// ======= CYCLISTE ÉNERVÉ =======

// Corps du cycliste (torse penché en avant, posture agressive)
ctx.fillStyle = '#ff6b35' // Maillot orange vif
ctx.fillRect(bikeCenter - 4, y + height - 35, 6, 12) // Torse

// Bras tendus vers le guidon (posture agressive)
ctx.strokeStyle = '#ffdbac' // Couleur peau
ctx.lineWidth = 2
ctx.beginPath()
ctx.moveTo(bikeCenter - 4, y + height - 32) // Épaule gauche
ctx.lineTo(bikeCenter + 2, y + height - 24) // Vers guidon gauche
ctx.stroke()
ctx.beginPath()
ctx.moveTo(bikeCenter + 4, y + height - 32) // Épaule droite
ctx.lineTo(bikeCenter + 12, y + height - 24) // Vers guidon droit
ctx.stroke()

// Mains agrippées au guidon (serrées fort)
ctx.fillStyle = '#ffdbac'
ctx.fillRect(bikeCenter + 1, y + height - 25, 2, 2) // Main gauche
ctx.fillRect(bikeCenter + 11, y + height - 25, 2, 2) // Main droite

// Jambes en mouvement de pédalage (une jambe tendue, l'autre pliée)
ctx.strokeStyle = '#2c3e50' // Pantalon sombre
ctx.lineWidth = 2
ctx.beginPath()
ctx.moveTo(bikeCenter - 2, y + height - 23) // Depuis le torse
ctx.lineTo(bikeCenter - 6, y + height - 10) // Vers pédale gauche (jambe tendue)
ctx.stroke()
ctx.beginPath()
ctx.moveTo(bikeCenter + 2, y + height - 23) // Depuis le torse
ctx.lineTo(bikeCenter - 1, y + height - 14) // Vers pédale droite (jambe pliée)
ctx.stroke()

// Tête avec expression de colère
ctx.fillStyle = '#ffdbac' // Couleur peau
ctx.beginPath()
ctx.arc(bikeCenter, y + height - 40, 4, 0, 2 * Math.PI) // Tête ronde
ctx.fill()

// Casque de vélo
ctx.fillStyle = '#ff0000' // Casque rouge vif
ctx.beginPath()
ctx.arc(bikeCenter, y + height - 41, 5, Math.PI, 2 * Math.PI) // Demi-cercle pour le casque
ctx.fill()

// Visage énervé - sourcils froncés
ctx.strokeStyle = '#8B4513'
ctx.lineWidth = 1
ctx.beginPath()
ctx.moveTo(bikeCenter - 2, y + height - 42) // Sourcil gauche froncé
ctx.lineTo(bikeCenter - 1, y + height - 41)
ctx.stroke()
ctx.beginPath()
ctx.moveTo(bikeCenter + 2, y + height - 42) // Sourcil droit froncé
ctx.lineTo(bikeCenter + 1, y + height - 41)
ctx.stroke()

// Yeux en colère (petits points noirs)
ctx.fillStyle = '#000'
ctx.fillRect(bikeCenter - 2, y + height - 40, 1, 1) // Œil gauche
ctx.fillRect(bikeCenter + 1, y + height - 40, 1, 1) // Œil droit

// Bouche ouverte en train de crier
ctx.fillStyle = '#8B0000'
ctx.fillRect(bikeCenter - 1, y + height - 37, 2, 2) // Bouche ouverte

// Lignes de colère autour de la tête (effet manga)
ctx.strokeStyle = '#ff4444'
ctx.lineWidth = 1
for (let i = 0; i < 3; i++) {
	const angle = (i * Math.PI) / 6
	ctx.beginPath()
	ctx.moveTo(bikeCenter + Math.cos(angle + Math.PI/4) * 6, y + height - 40 + Math.sin(angle + Math.PI/4) * 6)
	ctx.lineTo(bikeCenter + Math.cos(angle + Math.PI/4) * 8, y + height - 40 + Math.sin(angle + Math.PI/4) * 8)
	ctx.stroke()
}

// Gouttes de sueur (effort et énervement)
ctx.fillStyle = '#87CEEB'
ctx.beginPath()
ctx.arc(bikeCenter + 3, y + height - 38, 1, 0, 2 * Math.PI)
ctx.fill()

ctx.restore()
}

// Fonction pour dessiner un croissant
export const drawCroissant = (ctx: CanvasRenderingContext2D, x: number, y: number): void => {
ctx.save()

// Croissant doré
ctx.fillStyle = '#DAA520'
ctx.fillRect(x + 2, y + 8, 12, 6) // Corps principal
ctx.fillRect(x + 14, y + 6, 8, 4) // Extrémité droite
ctx.fillRect(x, y + 10, 8, 4) // Extrémité gauche
ctx.fillRect(x + 4, y + 6, 8, 4) // Partie haute
ctx.fillRect(x + 6, y + 12, 8, 4) // Partie basse

// Détails de texture
ctx.fillStyle = '#B8860B'
ctx.fillRect(x + 3, y + 9, 1, 1)
ctx.fillRect(x + 6, y + 7, 1, 1)
ctx.fillRect(x + 9, y + 11, 1, 1)
ctx.fillRect(x + 12, y + 9, 1, 1)

ctx.restore()
}
